import { tourSteps } from "@/context/tour/tutorial-tour";
import ConductionAPI, { ArtistStats } from "@/javascript/conduction-api";
import { Artist, Genre, Song } from "@/javascript/library-objects";
import MidiPlayer from "@/javascript/midi-player";
import { Instrument } from "@/javascript/music-objects";
import { ProjectSubmission } from "@/javascript/project-objects";
import { theme } from "@/theme";
import { IconAlertCircle, IconUserCheck } from "@tabler/icons-react";
import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Confetti from 'react-confetti';
import Joyride, { CallBackProps, STATUS, StoreHelpers } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { User } from "../javascript/user-objects";
import { Utils } from "../javascript/utils";

export interface SessionContextType {
	user: User | null;
	token: string | null;
	userSongs: Song[] | null;
	projectSubmissions: ProjectSubmission[] | null;
	fetchProjectSubmissions: () => Promise<void>;
	marketplaceSongs: Song[] | null;
	songsInCart: Song[] | null;
	setSongsInCart: (songs: Song[]) => void;
	songsInWishlist: Song[] | null;
	setSongsInWishlist: (songs: Song[]) => void;
	userInterests: UserInterests | null;
	setUserInterests: (interests: UserInterests) => void;
	artistStats: ArtistStats | null;
	setArtistStats: (artistStats: ArtistStats) => void;
	login: (username: string, password: string, rememberMe: boolean) => Promise<boolean>;
	logout: () => void;
	startTour: () => void
	tutorialTourActive: boolean,
	currentTourStep: number,
	moveToNextTourStep: () => void,
	moveToPrevTourStep: () => void,
	jumpToTourStep: (index: number) => void,
	fetchArtistStats: (artistId: string) => void
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined);

export type SkillLevel = "Beginner" | "Intermediate" | "Advanced" | null;

export interface UserInterests {
	artists: Artist[];
	genres: Genre[];
	instruments: Instrument[];
	skillLevel?: SkillLevel;
}

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	// Session values
	const [user, setUser] = useState<User | null>(null);
	const [userSongs, setUserSongs] = useState<Song[] | null>(null);
	const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[] | null>(null);
	const [marketplaceSongs, setMarketplaceSongs] = useState<Song[] | null>(null);
	const [songsInCart, setSongsInCart] = useState<Song[] | null>(null);
	const [songsInWishlist, setSongsInWishlist] = useState<Song[] | null>([]);
	const [userInterests, setUserInterests] = useState<UserInterests | null>(null);
	const [artistStats, setArtistStats] = useState<ArtistStats | null>(null)

	// Stored session values
	const getStoredUserId = () => sessionStorage.getItem("userId") || localStorage.getItem("userId");
	const getStoredToken = () => sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
	const [userId, setUserId] = useState<string | null>(getStoredUserId());
	const [token, setToken] = useState<string | null>(getStoredToken());

	// Tutorial tour management
	const [currentTourStep, setCurrentTourStep] = useState<number>(0);
	const [tutorialTourActive, setTutorialTourActive] = useState(false);
	const tourHelpersRef = useRef<StoreHelpers | null>(null);

	const navigate = useNavigate();

	// Fetch the current user based on stored token
	const fetchUser = async () => {
		if (!userId || !token) {
			return;
		}

		try {
			const user = await ConductionAPI.getUser(userId, token)
			if (user != undefined) {
				setUser(user);
			} else {
				logout(); // Clear token if invalid
			}
		} catch (error) {
			console.error("Error fetching user:", error);
			logout(); // Clear token if invalid
		}
	};

	const fetchUserSongs = useCallback(async () => {
		if (user === null || token === null || user.roles.includes("Guest")) {
			// User songs can only be fetched after login and not for guests
			return;
		}

		try {
			const loadedSongs = await ConductionAPI.getUserSongs(token);

			if (!loadedSongs) {
				return;
			}
			setUserSongs(loadedSongs.sort((a, b) => b.uploadDate.getTime() - a.uploadDate.getTime())); // Newest first

		} catch (error) {
			console.error('Error fetching songs:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, token]);

	const fetchProjectSubmissions = useCallback(async () => {
		if (user === null || token === null || user.roles.includes("Guest")) {
			// User submissions can only be fetched after login and not for guests
			return;
		}

		try {
			const loadedSubmissions = await ConductionAPI.getProjectSubmissions(token);

			if (!loadedSubmissions) {
				return;
			}
			setProjectSubmissions(loadedSubmissions);
		} catch (error) {
			console.error('Error fetching project submissions:', error);
			Utils.displayNotification(
				'Project Submissions Load Failure',
				'Something went wrong when loading your project submissions. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, token]);

	const fetchMarketplaceSongs = useCallback(async () => {
		try {
			const loadedSongs = await ConductionAPI.getMarketplaceSongs();

			if (!loadedSongs) {
				return;
			}
			setMarketplaceSongs(loadedSongs);
		} catch (error) {
			console.error('Error fetching songs:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, []);

	const fetchArtistStats = useCallback(async (artistId: string) => {
		try {
			if (user == null) {
				return;
			}

			const loadedStats = await ConductionAPI.getArtistStats(artistId);

			if (!loadedStats) {
				return;
			}
			setArtistStats(loadedStats);
		} catch (error) {
			console.error('Error fetching stats:', error);
		}
	}, [user]);


	// Load songs when the user changes
	useEffect(() => {
		fetchUserSongs();
		fetchProjectSubmissions();
		fetchMarketplaceSongs();
	}, [user, token]);

	// Load user when token changes
	useEffect(() => {
		if (userId === null) {
			setUser(null);
			setToken(null);
			return;
		}
		fetchUser();
	}, [userId, token]);

	// Login function for regular user
	const login = async (username: string, password: string, rememberMe: boolean): Promise<boolean> => {
		const notificaitonId = Utils.displayNotification("Attempting Login...", "This may take a few seconds.", "blue", "loading");
		try {
			const loginResults = await ConductionAPI.login(username, password, rememberMe);
			if (loginResults === null) {
				const icon = <IconAlertCircle size={16} />;
				Utils.updateNotification(notificaitonId, "Login Failed", "The provided username or password was incorrect.", "red", icon);
				return false;
			}
			const loggedInUser = loginResults.user;
			const loggedInToken = loginResults.token;

			setUserId(loggedInUser.id)
			setToken(loggedInToken);

			// Store token based on rememberMe preference
			if (rememberMe) {
				localStorage.setItem("userId", loggedInUser.id)
				localStorage.setItem("authToken", loggedInToken);
			} else {
				sessionStorage.setItem("userId", loggedInUser.id);
				sessionStorage.setItem("authToken", loggedInToken);
			}

			const icon = <IconUserCheck size={16} />;
			Utils.updateNotification(notificaitonId, "Login Successful", "Welcome back to Conduction!", "green", icon);
			return true;
		} catch (error) {
			const icon = <IconAlertCircle size={16} />;
			Utils.updateNotification(notificaitonId, "App Failure", "We are sorry, but an error has occurred during the login process. Please try again later.", "red", icon, true, false);
			console.error("Login failed:", error);
			return false;
		}
	};

	// Logout function
	const logout = async () => {
		MidiPlayer.stop();

		setUser(null);
		setUserId(null);
		setToken(null);
		setUserSongs(null);
		setSongsInWishlist(null);
		setSongsInCart([])
		setUserInterests(null);

		// Clear the cached-project notification if it is present
		Utils.updateNotification("cached-project", "", "undefined", "blue", <></>, false, 1);

		localStorage.removeItem("userId");
		sessionStorage.removeItem("userId");
		localStorage.removeItem("authToken");
		sessionStorage.removeItem("authToken");

		// Navigate to the home page
		navigate("/");
	};

	// ----------------------
	// Cart management
	// ----------------------

	useEffect(() => {
		// Remember the song IDs in the cart between sessions
		if (songsInCart && userId) {
			localStorage.setItem(`songIdsInCart_${userId}`, songsInCart.map(s => s.songId).join(";"));
		}
	}, [songsInCart]);

	useEffect(() => {
		// Load the cart from local storage when the component mounts and the marketplace songs are available
		if (marketplaceSongs && userId) {
			const storedSongIds = localStorage.getItem(`songIdsInCart_${userId}`);
			if (storedSongIds) {
				const songIds = storedSongIds.split(";").filter(id => id.trim() !== "");
				const songsInCart = marketplaceSongs.filter(song => songIds.includes(song.songId));
				setSongsInCart(songsInCart);
			}
		}
	}, [marketplaceSongs, userId]);

	// ----------------------
	// Artist statw management
	// ----------------------

	useEffect(() => {
		// Load from storage on mount
		const cachedStats = localStorage.getItem("artistStats");
		if (cachedStats) {
			setArtistStats(JSON.parse(cachedStats));
		}
	}, []);

	useEffect(() => {
		// Cache stats on change
		if (artistStats) {
			localStorage.setItem("artistStats", JSON.stringify(artistStats));
		}
	}, [artistStats]);

	// ----------------------
	// Tour management
	// ----------------------

	const startTour = () => {
		setTutorialTourActive(true);
		jumpToTourStep(0);
	};

	const handleGetHelpers = (helpers: StoreHelpers) => {
		tourHelpersRef.current = helpers;
	};

	const moveToNextTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.next();
		}
	}

	const moveToPrevTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.prev();
		}
	}

	const jumpToTourStep = (index: number) => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.go(index);
		}
	};

	// Callback to handle tour events (e.g., finish or skip)
	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, index } = data;
		setCurrentTourStep(index);
		const tutorialFinished = status === STATUS.FINISHED;
		const tutorialSkipped = status === STATUS.SKIPPED;
		if (tutorialFinished || tutorialSkipped) {
			if (tutorialFinished) {
				confettiTime();
			}
			if (user !== null && token !== null && !user.roles.includes("Guest")) {
				// If the tutorial was actually completed or if the tutorial was skipped and the user has a project saved, we cam set the completion status true
				if (tutorialFinished || (tutorialSkipped && user.projectIds.length > 0)) {
					ConductionAPI.setTutorialCompletionStatus(user.id, true, token)
				}
			}
		}
	};

	// ----------------------
	// Bells and whistles
	// ----------------------
	const [showConfetti, setShowConfetti] = useState(false);

	const confettiTime = () => {
		setShowConfetti(true);
	}

	return (
		<SessionContext.Provider
			value={{
				user,
				token,
				userSongs,
				projectSubmissions,
				fetchProjectSubmissions,
				marketplaceSongs,
				songsInCart,
				setSongsInCart,
				songsInWishlist,
				setSongsInWishlist,
				userInterests,
				setUserInterests,
				artistStats,
				setArtistStats,
				login,
				logout,
				startTour,
				tutorialTourActive,
				currentTourStep,
				moveToNextTourStep,
				moveToPrevTourStep,
				jumpToTourStep,
				fetchArtistStats
			}}
		>
			{/* React Joyride component for our tour */}
			<Joyride
				steps={tourSteps}
				run={tutorialTourActive}
				disableCloseOnEsc
				disableOverlayClose
				disableScrolling
				disableScrollParentFix
				continuous
				callback={handleJoyrideCallback}
				getHelpers={handleGetHelpers}
				styles={{
					options: {
						primaryColor: (theme.colors?.red)?.[6] // Custom color for the next button
					},
				}}
			/>

			{showConfetti &&
				<Confetti
					numberOfPieces={200}
					recycle={false}
					onConfettiComplete={() => setShowConfetti(false)}
				/>
			}

			{children}
		</SessionContext.Provider>
	);
};